import axiosInstance from '@/axiosInstance'
import User from '@/structs/User'
import LoginCredential from '@/structs/LoginCredential'
import SignupData from '@/structs/SignupData'
import Org from '@/structs/Org'
import Group from '@/structs/Group'
import router from '@/router'

const state = {
  user: new User(),
  org: new Org(),
  groups: [],
  didLoadInitialState: false
}

const getters = {
  isloggedin: state => {
    return state.user.id !== null && state.user.id.length > 0
  },
  isorgadmin: state => {
    return (state.groups || []).some(group => { return group.groupname === 'ADMINS' })
  },
  didLoadInitialState: state => {
    return state.didLoadInitialState
  },
  currentUser: state => {
    return state.user
  },
  currentOrg: state => {
    return state.org
  },
  currentGroups: state => {
    return state.groups
  }
}

const _processAuthData = (commit, response) => {
  const user = Object.assign(new User(), response.data.user)
  const org = Object.assign(new Org(), response.data.org)
  const groups = (response.data.groups || []).map(group => { return Object.assign(new Group(), group) })

  commit('SET_AUTH_USER', user)
  commit('SET_AUTH_ORG', org)
  commit('SET_AUTH_GROUPS', groups)
}

const actions = {
  authenticate ({ commit, getters, dispatch }, forceAjax) {
    return new Promise((resolve, reject) => {
      forceAjax = (forceAjax || false)
      if (forceAjax !== true && getters.didLoadInitialState !== true) {
        dispatch('loadInitialState')
          .then(() => { resolve() })
          .catch(() => {
            dispatch('authenticate', true)
              .then(() => { resolve() })
              .catch(() => { reject(new Error('Unable to authenticate')) })
          })
      } else {
        return axiosInstance.get(
          // '/data/auth-authenticate-success-loggedin.json'
          process.env.BASE_URL + 'api/?mode=AUTH&action=AUTHENTICATE'
        ).then(response => {
          if (response.data.user == null) {
            throw Error(`Not authenticated`)
          }

          _processAuthData(commit, response)
        }).then(() => { resolve() })
          .catch(() => { reject(new Error('Unable to authenticate')) })
      }
    })
  },
  login ({ commit }, credential) {
    if (!credential || !(credential instanceof LoginCredential)) {
      throw Error(`Invalid credential - ${credential}`)
    }

    // should be POST
    return axiosInstance.post(
      process.env.BASE_URL + 'api/?mode=AUTH&action=LOGIN',
      credential
    ).then(response => {
      if (!response.data.selectorg) {
        _processAuthData(commit, response)

        router.push({ name: 'home' })
      }

      return response
    })
  },
  logout ({ commit }) {
    // should be POST
    return axiosInstance.post(
      process.env.BASE_URL + 'api/?mode=AUTH&action=LOGOUT'
    ).then(() => {
      const user = new User()
      const org = new Org()
      const groups = []

      commit('SET_AUTH_USER', user)
      commit('SET_AUTH_ORG', org)
      commit('SET_AUTH_GROUPS', groups)

      window.location.href = process.env.BASE_URL
    })
  },
  signup ({ commit }, signupdata) {
    if (!signupdata || !(signupdata instanceof SignupData)) {
      throw Error(`Invalid signup data - ${signupdata}`)
    }

    // should be POST
    return axiosInstance.post(
      process.env.BASE_URL + 'api/?mode=AUTH&action=SIGNUP',
      signupdata
    ).then(response => {

      // router.push({ name: 'home' })
    })
  },
  activate ({ commit }, token) {
    return axiosInstance.post(
      process.env.BASE_URL + 'api/?mode=AUTH&action=ACTIVATE',
      { token: token }
    ).then(response => {
      _processAuthData(commit, response)

      router.push({ name: 'home' })
    })
  },
  joinOrgResponse ({ commit }, { token }) {
    return new Promise((resolve, reject) => {
      return axiosInstance.post(
        process.env.BASE_URL + 'api/?mode=AUTH&action=JOINORGRESPONSE',
        { token: token }
      ).then(response => {
        const user = Object.assign(new User(), response.data.user)
        const org = Object.assign(new Org(), response.data.org)
        const accepted = response.data.accepted

        resolve({ accepted, user, org })
      }).catch((err) => { reject(err) })
    })
  },
  sendResetRequest ({ commit }, { email }) {
    return new Promise((resolve, reject) => {
      return axiosInstance.post(
        process.env.BASE_URL + 'api/?mode=AUTH&action=RESET',
        { email: email }
      ).then(response => {
        resolve(response.data.email)
      }).catch((err) => { reject(err) })
    })
  },
  validateToken ({ commit }, { token }) {
    return new Promise((resolve, reject) => {
      return axiosInstance.post(
        process.env.BASE_URL + 'api/?mode=AUTH&action=VALIDATETOKEN',
        { token: token }
      ).then(response => {
        resolve(response.data)
      }).catch((err) => { reject(err) })
    })
  },
  completeResetRequest ({ commit }, { token, password }) {
    return new Promise((resolve, reject) => {
      return axiosInstance.post(
        process.env.BASE_URL + 'api/?mode=AUTH&action=COMPLETERESET',
        {
          token: token,
          password: password
        }
      ).then(response => {
        resolve(response)
      }).catch((err) => { reject(err) })
    })
  },
  loadInitialState ({ commit }) {
    return new Promise((resolve, reject) => {
      commit('SET_AUTH_INITIALSTATE', true)
      if (typeof (window.initialState) !== 'object') {
        reject(new Error('window.initialState not an object'))
        return
      }

      const isValidUser = (user) => {
        if (user == null) return false
        if (typeof (user) !== 'object') return false
        if (!user.hasOwnProperty('id')) return false
        if (user.id == null) return false
        if (user.id.length === 0) return false

        return true
      }

      const isValidOrg = (org) => {
        if (org == null) return false
        if (typeof (org) !== 'object') return false
        if (!org.hasOwnProperty('id')) return false
        if (org.id == null) return false
        if (org.id.length === 0) return false

        return true
      }

      if (window.initialState.hasOwnProperty('user') && isValidUser(window.initialState.user)) {
        const user = Object.assign(new User(), window.initialState.user)
        commit('SET_AUTH_USER', user)

        // they don't have a user, they shouldn't have an org
        if (window.initialState.hasOwnProperty('org') && isValidOrg(window.initialState.org)) {
          const org = Object.assign(new Org(), window.initialState.org)
          commit('SET_AUTH_ORG', org)

          const groups = []
          commit('SET_AUTH_GROUPS', groups)
        }

        resolve()
      } else {
        reject(new Error('Invalid or empty window.initialState.user'))
      }
    })
  }
}

const mutations = {
  SET_AUTH_USER: (state, user) => {
    state.user = user
  },
  SET_AUTH_ORG: (state, org) => {
    state.org = org
  },
  SET_AUTH_GROUPS: (state, groups) => {
    state.groups = groups
  },
  SET_AUTH_INITIALSTATE: (state, didload) => {
    state.didLoadInitialState = didload
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
